import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import clsx from 'clsx';
import { fontWeight, Text, TextError } from '../../../../../packages/ui/atoms/typography';
import { Input, inputSize } from '../../../../../packages/ui/atoms/input';
import { Icons } from '../../../../../packages/icons';
import { colors } from '../../../../../packages/colors';
import { searchPharmacies } from '../../../../../services/schedule/schedule.service';
import { showSnackbar } from '../../../../../redux/modules/snackbar';
import { useDispatch } from 'react-redux';
import { useStyles } from './StepThreePharmacy.styles';

const StepThreePharmacy = ({
  selectedPharmacy,
  onPharmacySelect,
  zipCode,
  existingPharmacy,
  memberPreferredPharmacy,
  disableAutoSelect = false,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState('');
  const [pharmacyZipCode, setPharmacyZipCode] = useState(zipCode);
  const [pharmacies, setPharmacies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [allPharmacies, setAllPharmacies] = useState([]);

  const fetchPharmacies = async () => {
    if (!pharmacyZipCode) {
      setError('Member zip code is required to search pharmacies');
      return;
    }

    setLoading(true);
    onPharmacySelect(null); // Clear any previously selected pharmacy
    setError('');

    try {
      const response = await searchPharmacies(pharmacyZipCode);
      if (response.data) {
        setAllPharmacies(response.data);
        setPharmacies(response.data);
      } else {
        setAllPharmacies([]);
        setPharmacies([]);
        setError('No pharmacies found against the provided zip code');
      }
    } catch (err) {
      console.error('Failed to search pharmacies:', err);
      setAllPharmacies([]);
      setPharmacies([]);
      setError('Failed to load pharmacies. Please try again.');
      dispatch(
        showSnackbar({
          snackType: 'error',
          snackMessage: 'Failed to load pharmacies',
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const filterPharmacies = query => {
    if (!query.trim()) {
      setPharmacies(allPharmacies);
      return;
    }
    const filteredPharmacies = allPharmacies.filter(
      pharmacy =>
        pharmacy.name?.toLowerCase().includes(query.toLowerCase()) ||
        pharmacy.address1?.toLowerCase().includes(query.toLowerCase()) ||
        pharmacy.address2?.toLowerCase().includes(query.toLowerCase()),
    );
    setPharmacies(filteredPharmacies);
  };

  const formatPharmacyAddress = pharmacy => {
    const parts = [];
    if (pharmacy.address2) parts.push(pharmacy.address2);
    if (pharmacy.address1) parts.push(pharmacy.address1);
    return parts.join(', ') || 'Address not available';
  };

  const formatPhoneNumber = phone => {
    if (!phone || phone.length !== 10) return phone;
    return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`;
  };

  useEffect(() => {
    if (pharmacyZipCode && pharmacyZipCode.length >= 5) {
      void fetchPharmacies();
    }
  }, [pharmacyZipCode]);

  // Preselect pharmacy based on priority: existing medication > member preferred > none
  useEffect(() => {
    if (disableAutoSelect || selectedPharmacy) return;
    if (pharmacies.length > 0) {
      let pharmacyToSelect = null;

      // Priority 1: Check if updating existing medication with pharmacy
      if (existingPharmacy?.id) {
        pharmacyToSelect = pharmacies.find(p => p.id === existingPharmacy.id);
        // If found, call onPharmacySelect to update parent state with complete data
        if (pharmacyToSelect) {
          onPharmacySelect(pharmacyToSelect);
          return; // Exit early since we've set the pharmacy
        }
      }

      // Priority 2: Check member's preferred pharmacy
      if (!pharmacyToSelect && memberPreferredPharmacy) {
        pharmacyToSelect = pharmacies.find(p => p.id === memberPreferredPharmacy);
        if (pharmacyToSelect) {
          onPharmacySelect(pharmacyToSelect);
        }
      }
    }
  }, [pharmacies, selectedPharmacy, existingPharmacy, memberPreferredPharmacy, onPharmacySelect]);

  const handleSearchChange = event => {
    const query = event.target.value;
    setSearchQuery(query);
    filterPharmacies(query);
  };

  const handleZipChange = event => {
    const zipValue = event.target.value;
    setPharmacyZipCode(zipValue);
  };

  const handlePharmacySelect = pharmacy => {
    onPharmacySelect(pharmacy);
  };

  // const IconBox = ({ glyph }) => (
  //   <Box
  //     sx={{
  //       width: '55px',
  //       height: '55px',
  //       borderRadius: '8px',
  //       backgroundColor: colors.primary50,
  //       border: `1px solid ${colors.primary300}`,
  //       display: 'flex',
  //       alignItems: 'center',
  //       justifyContent: 'center',
  //       flexShrink: 0,
  //     }}
  //   >
  //     <Icons glyph={glyph} color={colors.primary500} width="32px" height="32px" />
  //   </Box>
  // );

  // const renderMemberAddressCard = () => (
  //   <Box className={classes.infoCard}>
  //     <Box display="flex" gap={2} alignItems="center">
  //       <IconBox glyph="member" />
  //       <Box display="flex" flexDirection="column" gap="4px">
  //         {memberAddress?.line1 && (
  //           <Text weight={fontWeight.SEMI_BOLD} level={textLevel.M}>
  //             {memberAddress.line1}
  //           </Text>
  //         )}
  //         {memberAddress?.line2 && (
  //           <Text level={textLevel.S} color={colors.neutral500}>
  //             {memberAddress.line2}
  //           </Text>
  //         )}
  //         {selectedMedications.map((item, index) => (
  //           <Text key={index} level={textLevel.S} color={colors.neutral500}>
  //             {item?.externalRx?.label || item.item?.label} - {item?.dose} - {item?.quantity}{' '}
  //             {item?.frequency?.title} for {item?.duration} day(s)
  //           </Text>
  //         ))}
  //       </Box>
  //     </Box>
  //   </Box>
  // );

  const renderPharmacyItem = (pharmacy, index) => {
    const isSelected = selectedPharmacy?.id === pharmacy.id;

    return (
      <Box
        key={pharmacy.id || index}
        className={clsx(classes.pharmacyItem, {
          [classes.selectedPharmacy]: isSelected,
        })}
        onClick={() => handlePharmacySelect(pharmacy)}
      >
        <Box className={classes.pharmacyInfo}>
          <Text weight={fontWeight.BOLD} className={classes.pharmacyName}>
            {pharmacy.name}
          </Text>
          <Text className={classes.pharmacyAddress}>{formatPharmacyAddress(pharmacy)}</Text>
          {pharmacy.phone && (
            <Text className={classes.pharmacyPhone}>{formatPhoneNumber(pharmacy.phone)}</Text>
          )}
          {pharmacy.open24hrs && <Text className={classes.pharmacyFeature}>Open 24 hours</Text>}
          {pharmacy.controlledSubstances && (
            <Text className={classes.pharmacyFeature}>Controlled substances available</Text>
          )}
        </Box>
        {isSelected && <Icons glyph="check-circle" color={colors.success} className={classes.selectedIcon} />}
      </Box>
    );
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.searchSection}>
        {/* {!isMedicationView && renderMemberAddressCard()} */}
        <Text weight={fontWeight.BOLD} className={classes.sectionTitle}>
          Choose Pharmacy
        </Text>
        <Box className={classes.inputsBox}>
          <Input
            value={searchQuery}
            size={inputSize.M}
            placeholder="Search for pharmacy here"
            onChange={handleSearchChange}
            startAdornment={<Icons glyph="search" color={colors.textSecondary} />}
            className={classes.searchInput}
          />
          <Input
            value={pharmacyZipCode}
            size={inputSize.M}
            placeholder="Enter Zipcode"
            onChange={handleZipChange}
            className={classes.zipCodeInput}
          />
        </Box>
        {error && <TextError errorMsg={error} />}
      </Box>

      <Box className={classes.pharmacyList}>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <Icons className="rotate linear infinite" glyph="in-progress" color={colors.primary} />
            <Text className={classes.sectionTitle}>Loading pharmacies...</Text>
          </Box>
        ) : pharmacies.length > 0 ? (
          pharmacies
            .sort((a, b) => {
              if (selectedPharmacy?.id === a.id) return -1;
              if (selectedPharmacy?.id === b.id) return 1;
              return 0;
            })
            .map(renderPharmacyItem)
        ) : (
          !loading && (
            <Box className={classes.emptyState}>
              <Text className={classes.sectionTitle}>No pharmacies found in your zip code</Text>
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export default StepThreePharmacy;
